import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import heroCameraMobile from "@/assets/hero-camera-400.jpg";
import heroCamera from "@/assets/hero-camera-800.jpg";
import ebooksImg from "@/assets/ebooks.jpg";
import courseBasicsMobile from "@/assets/course-basics-400.jpg";
import courseBasics from "@/assets/course-basics-800.jpg";
import courseMobileMobile from "@/assets/course-mobile-400.jpg";
import courseMobile from "@/assets/course-mobile-800.jpg";
import courseEditingMobile from "@/assets/course-editing-400.jpg";
import courseEditing from "@/assets/course-editing-800.jpg";
import courseCompleteMobile from "@/assets/course-complete-400.jpg";
import courseComplete from "@/assets/course-complete-800.jpg";
import courseProductMobile from "@/assets/course-product-400.jpg";
import courseProduct from "@/assets/course-product-800.jpg";
import { blogPosts } from "@/content/blog";
import {
  Camera,
  Smartphone,
  Wand2,
  GraduationCap,
  Package,
  Star,
  ArrowRight,
  Check,
  Menu,
  X,
  BookOpen,
} from "lucide-react";

declare global {
  interface Window {
    hbspt?: { forms: { create: (opts: Record<string, unknown>) => void } };
  }
}

const courses = [
  {
    n: "01",
    icon: GraduationCap,
    title: "Aprende fotografía digital desde cero. De novato a Pro",
    desc: "Fundamentos, exposición, enfoque y composición para empezar con una base sólida.",
    level: "Principiante",
    url: "https://www.udemy.com/course/aprende-fotografia-digital/?referralCode=83D2EE0525CA7854421B",
    image: courseBasics,
    imageMobile: courseBasicsMobile,
  },
  {
    n: "02",
    icon: Smartphone,
    title: "Fotografía con móvil. Domina la cámara del teléfono",
    desc: "Controla la cámara del móvil y consigue imágenes más cuidadas en cualquier situación.",
    level: "Todos los niveles",
    url: "https://www.udemy.com/course/fotografia-movil-entiende-y-domina-la-camara-de-tu-celular/?referralCode=E77EF8DF89C02DD1E28B",
    image: courseMobile,
    imageMobile: courseMobileMobile,
  },
  {
    n: "03",
    icon: Wand2,
    title: "Edición de fotografías PRO con Luminar NEO",
    desc: "Flujo de edición completo con herramientas potenciadas por inteligencia artificial.",
    level: "Intermedio",
    url: "https://www.udemy.com/course/edicion-de-fotografias-luminar-neo/?referralCode=B746C994F97CF69757AD",
    image: courseEditing,
    imageMobile: courseEditingMobile,
  },
  {
    n: "04",
    icon: Camera,
    title: "Curso completo de fotografía digital",
    desc: "Formación integral para dominar técnica, composición y práctica fotográfica.",
    level: "Todos los niveles",
    url: "https://www.udemy.com/course/fotografia-digital-desde-cero/?referralCode=8A311BBCEE3311D1B685",
    image: courseComplete,
    imageMobile: courseCompleteMobile,
  },
  {
    n: "05",
    icon: Package,
    title: "Fotografía de producto con la cámara del teléfono",
    desc: "Crea imágenes de producto eficaces para comercio electrónico y redes sociales.",
    level: "Práctico",
    url: "https://www.udemy.com/course/fotografia-de-producto-con-celular/?referralCode=EF0DE4C988C456216552",
    image: courseProduct,
    imageMobile: courseProductMobile,
  },
];

const featuredCourse = courses.find((course) => course.n === "04") ?? courses[0];
const FeaturedCourseIcon = featuredCourse.icon;
const secondaryCourses = courses.filter((course) => course.n !== featuredCourse.n);
const orderedCourses = [featuredCourse, ...secondaryCourses];
const latestBlogPosts = blogPosts.slice(0, 3);

const testimonials = [
  {
    name: "Francisco Medina",
    initials: "FM",
    rating: 5,
    quote: "Estoy iniciándome en la fotografía y me ha sido de mucha utilidad.",
  },
  {
    name: "Jesus Pina",
    initials: "JP",
    rating: 5,
    quote:
      "Muy buen curso, dando buenos tips desde la experiencia que tiene el maestro como fotógrafo. Las opciones de practicas me resultaron muy interesantes. Felicidades por sus ganas y deseos de enseñar.",
  },
  {
    name: "Pablo Hernandez",
    initials: "PH",
    rating: 5,
    quote:
      "Porque la explicación del maestro es muy clara y sencilla de entender sobre todo para personas como yo que estamos prácticamente de 0",
  },
  {
    name: "Gema Noval",
    initials: "GN",
    rating: 4.5,
    quote:
      "Curso bastante bien explicado. Me ha aclarado muchos conceptos que tenía en el aire y me han gustado mucho los contenidos.",
  },
  {
    name: "Gissel Peña",
    initials: "GP",
    rating: 5,
    quote:
      "Hola, gracias estoy conociendo mi cámara del celular, ha sido una nueva experiencia, espero seguir aprendiendo a explorar mi cámara. Me ha gustado, gracias por compartir sus conocimientos.",
  },
  {
    name: "Laura Renzetti",
    initials: "LR",
    rating: 5,
    quote:
      "Me resultó muy didáctico el docente, muy completo el contenido e intuitivo de seguir con facilidad y geniales los recursos extras. Lo tomé para refrescar conceptos y superó mis expectativas. Muchas gracias hasta el próximo.",
  },
];

const faqs = [
  {
    q: "¿Necesito una cámara profesional?",
    a: "¡En absoluto! Los cursos de fotografía que tienes en este sitio son cursos de iniciación. Vale cualquier cámara que tenga el modo manual y los modos semiautomáticos.",
  },
  {
    q: "¿Los cursos son adecuados para principiantes?",
    a: "Sí. Estos cursos están pensados precisamente para empezar desde cero en la fotografía.",
  },
  {
    q: "¿Cómo accedo a los cursos?",
    a: "Cada ficha enlaza directamente con la página correspondiente del curso en Udemy.",
  },
  {
    q: "¿Qué recibiré al registrarme?",
    a: "Dos eBooks: una guía de composición y otra sobre nitidez fotográfica.",
  },
  {
    q: "¿Puedo aprender a editar mis fotografías?",
    a: "Sí. El curso de Luminar NEO está orientado a un flujo de edición completo.",
  },
];

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "FotoPasión · Cursos de fotografía online" },
      {
        name: "description",
        content:
          "Cursos online prácticos de fotografía: fundamentos, móvil, edición con Luminar NEO y fotografía de producto.",
      },
      { property: "og:title", content: "FotoPasión · Cursos de fotografía online" },
      {
        property: "og:description",
        content:
          "Cursos online prácticos de fotografía: fundamentos, móvil, edición con Luminar NEO y fotografía de producto.",
      },
      { property: "og:url", content: "https://fotopasion.lovable.app/" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        property: "og:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/76mMWoFnwBeqz3V4BgQMahjpK1l1/social-images/social-1785056547127-Regalo-ebooks-fotografia.webp",
      },
      {
        name: "twitter:image",
        content:
          "https://storage.googleapis.com/gpt-engineer-file-uploads/76mMWoFnwBeqz3V4BgQMahjpK1l1/social-images/social-1785056547127-Regalo-ebooks-fotografia.webp",
      },
    ],
    links: [{ rel: "canonical", href: "https://fotopasion.lovable.app/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Cursos de fotografía de FotoPasión",
          itemListElement: orderedCourses.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Course",
              name: c.title,
              description: c.desc,
              url: c.url,
              inLanguage: "es-ES",
              provider: {
                "@type": "Organization",
                name: "FotoPasión",
                url: "https://fotopasion.lovable.app",
              },
            },
          })),
        }),
      },
    ],
  }),
  component: Index,
});

function HubspotForm({
  formId,
  containerId = "hubspot-form-container",
  buttonLabel = "Cargar formulario",
}: {
  formId: string;
  containerId?: string;
  buttonLabel?: string;
}) {
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = containerRef.current;
    if (!target || shouldLoad || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "240px 0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [shouldLoad]);

  useEffect(() => {
    if (!shouldLoad) return;

    const id = "hs-forms-embed-v2";
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | undefined;
    const render = () => {
      if (cancelled || !window.hbspt) return false;
      const target = document.getElementById(containerId);
      if (!target) return false;
      target.innerHTML = "";
      window.hbspt.forms.create({
        portalId: "143612530",
        formId,
        region: "eu1",
        target: `#${containerId}`,
      });
      return true;
    };
    const waitAndRender = () => {
      if (render()) return;
      timer = setInterval(() => {
        if (render() && timer) clearInterval(timer);
      }, 200);
    };
    if (!document.getElementById(id)) {
      const s = document.createElement("script");
      s.id = id;
      s.src = "https://js-eu1.hsforms.net/forms/embed/v2.js";
      s.charset = "utf-8";
      s.async = true;
      document.body.appendChild(s);
    }
    const existingScript = document.getElementById(id) as HTMLScriptElement | null;
    if (existingScript && !window.hbspt) {
      existingScript.addEventListener("load", waitAndRender, { once: true });
    } else {
      waitAndRender();
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      existingScript?.removeEventListener("load", waitAndRender);
    };
  }, [formId, containerId, shouldLoad]);

  return (
    <div ref={containerRef} id={containerId} className="min-h-[220px]" aria-live="polite">
      {!shouldLoad && (
        <div className="flex min-h-[220px] flex-col items-center justify-center rounded-2xl bg-secondary/5 p-6 text-center">
          <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
            El formulario se cargará al acercarte a esta sección. También puedes abrirlo ahora.
          </p>
          <button
            type="button"
            onClick={() => setShouldLoad(true)}
            className="mt-4 inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:brightness-90"
          >
            {buttonLabel}
          </button>
        </div>
      )}
    </div>
  );
}

const stats = [
  { value: 10000, prefix: "+", suffix: "", label: "Alumnos en Udemy" },
  { value: 81, prefix: "", suffix: "", label: "Países según Udemy" },
];

const numberFormatter = new Intl.NumberFormat("es-ES");

function CountUp({
  end,
  prefix = "",
  suffix = "",
  duration = 1800,
}: {
  end: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
}) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(end * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration]);
  return (
    <>
      {prefix}
      {numberFormatter.format(n)}
      {suffix}
    </>
  );
}

function StatsRibbon() {
  return (
    <div className="bg-secondary text-secondary-foreground" aria-label="Datos de alcance en Udemy">
      <div className="mx-auto grid max-w-4xl grid-cols-2 divide-x divide-white/15 px-6 py-4 md:py-5">
        {stats.map((s) => (
          <div key={s.label} className="px-4 text-center">
            <div className="font-display text-4xl font-light tracking-tight md:text-5xl">
              <CountUp end={s.value} prefix={s.prefix} suffix={s.suffix} />
            </div>
            <div className="mt-1 text-sm font-medium text-cta">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const navLinks = [
  { href: "#cursos", label: "Cursos" },
  { href: "#recursos", label: "Recursos gratuitos" },
  { href: "#faq", label: "Preguntas frecuentes" },
];

function Index() {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background font-sans text-foreground">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-lg">
        <nav className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-6 py-4">
          <a href="#top" className="flex min-w-0 items-center gap-2">
            <img
              src="/logo.png"
              alt="FotoPasión"
              width={96}
              height={96}
              className="h-12 w-auto shrink-0"
            />
            <span className="truncate text-lg font-bold text-foreground">FotoPasion</span>
          </a>
          <div className="hidden items-center gap-8 text-sm font-medium md:flex">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-muted-foreground transition hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
            <Link to="/blog" className="text-muted-foreground transition hover:text-foreground">
              Blog
            </Link>
            <Link
              to="/guia-principiantes"
              className="text-muted-foreground transition hover:text-foreground"
            >
              Guía para principiantes
            </Link>
          </div>
          <a
            href="#cursos"
            className="hidden shrink-0 items-center gap-2 rounded-full bg-cta px-5 py-2.5 text-sm font-semibold text-cta-foreground shadow-[var(--shadow-soft)] transition hover:brightness-95 md:inline-flex"
          >
            Ver cursos <ArrowRight className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={menuOpen}
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border bg-card text-foreground transition hover:border-primary/40 md:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
        {menuOpen && (
          <div className="border-t border-border/60 bg-background md:hidden">
            <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4 text-base font-medium">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-xl px-2 py-3 text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/blog"
                onClick={() => setMenuOpen(false)}
                className="rounded-xl px-2 py-3 text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
              >
                Blog
              </Link>
              <Link
                to="/guia-principiantes"
                onClick={() => setMenuOpen(false)}
                className="rounded-xl px-2 py-3 text-muted-foreground transition hover:bg-secondary/10 hover:text-foreground"
              >
                Guía para principiantes
              </Link>
              <a
                href="#cursos"
                onClick={() => setMenuOpen(false)}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-cta px-5 py-3 text-sm font-semibold text-cta-foreground shadow-[var(--shadow-soft)]"
              >
                Ver cursos <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* HERO */}
        <section
          id="top"
          className="relative overflow-hidden"
          style={{ background: "var(--gradient-hero)" }}
        >
          <div className="absolute inset-0" style={{ background: "var(--gradient-hero-glow)" }} />
          <div className="relative mx-auto grid max-w-7xl gap-8 px-6 py-12 md:grid-cols-2 md:grid-rows-[auto_auto] md:gap-x-12 md:gap-y-0 md:px-8 md:py-20">
            <div className="md:col-start-1 md:row-start-1 md:self-end">
              <span className="inline-flex items-center rounded-full bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary backdrop-blur">
                Aprende fotografía a tu ritmo
              </span>
              <h1 className="mt-6 text-balance font-display text-[2.75rem] font-bold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
                Convierte tu cámara en una herramienta{" "}
                <span className="italic text-primary">creativa</span>
              </h1>
            </div>
            <div className="relative md:col-start-2 md:row-span-2 md:row-start-1 md:self-center">
              <div
                className="absolute -inset-4 rounded-3xl opacity-60 blur-2xl"
                style={{ background: "var(--gradient-primary)" }}
              />
              <picture>
                <source media="(max-width: 767px)" srcSet={heroCameraMobile} />
                <img
                  src={heroCamera}
                  alt="Cámara fotográfica en manos de un fotógrafo"
                  width={800}
                  height={450}
                  fetchPriority="high"
                  decoding="async"
                  className="relative aspect-video w-full rounded-3xl object-cover shadow-[var(--shadow-soft)]"
                />
              </picture>
            </div>
            <div className="md:col-start-1 md:row-start-2 md:self-start">
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
                Cursos online prácticos, claros y pensados para ayudarte a avanzar desde los
                fundamentos hasta la edición y la fotografía de producto.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap">
                <a
                  href="#cursos"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-cta px-7 py-3.5 font-semibold text-cta-foreground shadow-[var(--shadow-soft)] transition hover:scale-[1.02] hover:brightness-95"
                >
                  Explorar cursos <ArrowRight className="h-4 w-4" />
                </a>
                <a
                  href="#recursos"
                  className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary bg-white/60 px-7 py-3.5 font-semibold text-primary backdrop-blur transition hover:bg-white"
                >
                  Recoge tus regalos
                </a>
              </div>
            </div>
          </div>
        </section>

        <StatsRibbon />

        {/* CURSOS */}
        <section id="cursos" className="mx-auto max-w-7xl px-6 py-16 md:px-8 md:py-24">
          <div className="mb-10 max-w-2xl md:mb-14">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
              Catálogo
            </span>
            <h2 className="mt-2 text-balance font-display text-4xl font-bold md:text-5xl">
              Cursos destacados
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Empieza por la formación completa o elige el curso que mejor encaja con tu objetivo.
            </p>
          </div>

          <a
            href={featuredCourse.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group grid overflow-hidden rounded-[2rem] bg-secondary text-secondary-foreground shadow-[var(--shadow-soft)] transition hover:-translate-y-1 hover:shadow-[var(--shadow-glow)] md:grid-cols-[1.05fr_0.95fr]"
          >
            <div className="aspect-[16/10] overflow-hidden md:aspect-auto md:min-h-[390px]">
              <picture>
                <source media="(max-width: 767px)" srcSet={featuredCourse.imageMobile} />
                <img
                  src={featuredCourse.image}
                  alt={featuredCourse.title}
                  loading="lazy"
                  width={800}
                  height={500}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
              </picture>
            </div>
            <div className="flex flex-col justify-center p-8 md:p-10">
              <div className="flex items-start gap-6">
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-cta">
                    <FeaturedCourseIcon className="h-6 w-6" />
                  </span>
                  <span className="rounded-full bg-coral px-3 py-1 text-xs font-semibold uppercase tracking-wider text-coral-foreground">
                    Curso principal
                  </span>
                </div>
              </div>
              <h3 className="mt-7 text-balance font-display text-3xl font-bold leading-tight md:text-4xl">
                {featuredCourse.title}
              </h3>
              <p className="mt-4 text-lg leading-relaxed text-secondary-foreground/75">
                {featuredCourse.desc}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Técnica", "Composición", "Práctica"].map((topic) => (
                  <span
                    key={topic}
                    className="rounded-full border border-white/20 px-3 py-1 text-sm text-white/85"
                  >
                    {topic}
                  </span>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <span className="w-fit rounded-full bg-cta/15 px-3 py-1 text-sm font-medium text-cta">
                  {featuredCourse.level}
                </span>
                <span className="inline-flex items-center justify-center gap-2 rounded-full bg-cta px-6 py-3 font-semibold text-cta-foreground transition group-hover:brightness-95">
                  Ver curso completo <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </a>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {secondaryCourses.map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.n}
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-primary/40 hover:shadow-[var(--shadow-soft)]"
                >
                  <div className="-mx-7 -mt-7 mb-6 aspect-[16/9] overflow-hidden">
                    <picture>
                      <source media="(max-width: 767px)" srcSet={c.imageMobile} />
                      <img
                        src={c.image}
                        alt={c.title}
                        loading="lazy"
                        width={800}
                        height={500}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    </picture>
                  </div>
                  <div className="flex items-start">
                    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-secondary text-cta">
                      <Icon className="h-6 w-6" />
                    </span>
                  </div>
                  <h3 className="mt-6 font-sans text-xl font-semibold leading-snug tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-base leading-relaxed text-muted-foreground">{c.desc}</p>
                  <div className="mt-auto flex flex-col gap-4 pt-8 sm:flex-row sm:items-center sm:justify-between">
                    <span className="rounded-full bg-accent/40 px-3 py-1 text-xs font-medium text-accent-foreground">
                      {c.level}
                    </span>
                    <span className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition group-hover:brightness-90">
                      Ver curso <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        </section>

        {/* LEAD MAGNET */}
        <section
          id="recursos"
          className="relative overflow-hidden"
          style={{ background: "var(--gradient-hero)" }}
        >
          <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 md:grid-cols-2 md:items-center md:gap-14 md:px-8 md:py-24">
            <div>
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                Recurso gratuito
              </span>
              <h2 className="mt-2 text-balance font-display text-4xl font-bold leading-tight md:text-5xl">
                Regístrate y recibe{" "}
                <span className="italic text-primary">los dos eBooks gratis</span>
              </h2>
              <ul className="mt-8 space-y-3 text-base">
                {["Guía de composición fotográfica", "Secretos para hacer fotos nítidas"].map(
                  (t) => (
                    <li key={t} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 grid h-6 w-6 place-items-center rounded-full text-primary-foreground"
                        style={{ background: "var(--gradient-primary)" }}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className="font-medium">{t}</span>
                    </li>
                  ),
                )}
              </ul>
              <div className="mt-10 overflow-hidden rounded-3xl shadow-[var(--shadow-soft)]">
                <img
                  src={ebooksImg}
                  alt="Portadas de los eBooks de fotografía"
                  width={1536}
                  height={1024}
                  loading="lazy"
                  className="w-full object-cover"
                />
              </div>
            </div>
            <div className="rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-soft)] md:p-10">
              <h3 className="font-sans text-2xl font-semibold tracking-tight">Quiero mis eBooks</h3>
              <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                Completa el formulario y recibirás los dos eBooks en tu correo.
              </p>
              <div className="mt-6">
                <HubspotForm
                  formId="421d735a-ea8d-4301-bfa4-3f63491b82f7"
                  containerId="hubspot-form-ebooks"
                  buttonLabel="Mostrar formulario para recibir los eBooks"
                />
              </div>
            </div>
          </div>
        </section>

        {/* BLOG */}
        <section id="blog" className="bg-secondary/10">
          <div className="mx-auto max-w-7xl px-6 py-16 md:px-8 md:py-24">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-2xl">
                <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                  Blog FotoPasión
                </span>
                <h2 className="mt-2 text-balance font-display text-4xl font-bold md:text-5xl">
                  Últimos artículos
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  Guías, consejos y ejercicios para entender mejor tu cámara, entrenar la mirada y
                  seguir avanzando.
                </p>
              </div>
              <Link
                to="/blog"
                className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full border border-primary/25 bg-card px-5 py-2.5 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary hover:text-primary-foreground"
              >
                Ver todo el blog <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {latestBlogPosts.length > 0 ? (
              <div className="mt-10 grid gap-6 md:mt-14 md:grid-cols-2 lg:grid-cols-3">
                {latestBlogPosts.map((post) => (
                  <article
                    key={post.slug}
                    className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:border-primary/35 hover:shadow-[var(--shadow-soft)]"
                  >
                    <img
                      src={post.featuredImage}
                      alt={post.featuredImageAlt}
                      width={800}
                      height={520}
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-[1.02]"
                    />
                    <div className="flex flex-1 flex-col p-7">
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        {post.category}
                      </span>
                      <h3 className="mt-3 text-balance font-display text-2xl font-bold leading-tight">
                        {post.title}
                      </h3>
                      <p className="mt-3 flex-1 leading-7 text-muted-foreground">
                        {post.description}
                      </p>
                      <Link
                        to="/blog/$slug"
                        params={{ slug: post.slug }}
                        aria-label={`Leer artículo: ${post.title}`}
                        className="mt-7 inline-flex items-center gap-2 font-semibold text-primary"
                      >
                        Leer artículo{" "}
                        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <article className="mt-10 grid overflow-hidden rounded-[2rem] border border-border bg-card shadow-sm md:mt-14 md:grid-cols-[0.9fr_1.1fr]">
                <img
                  src={heroCamera}
                  alt="Cámara fotográfica preparada para aprender"
                  width={800}
                  height={450}
                  loading="lazy"
                  className="aspect-video h-full min-h-[260px] w-full object-cover md:aspect-auto"
                />
                <div className="flex flex-col justify-center p-8 md:p-10 lg:p-12">
                  <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                    <BookOpen className="h-4 w-4" /> En preparación
                  </span>
                  <h3 className="mt-5 text-balance font-display text-3xl font-bold leading-tight md:text-4xl">
                    Los primeros artículos llegarán pronto
                  </h3>
                  <p className="mt-4 max-w-2xl text-lg leading-7 text-muted-foreground">
                    Estamos preparando contenidos prácticos sobre técnica, composición, fotografía
                    móvil y edición. Mientras tanto, ya puedes visitar la portada del blog.
                  </p>
                  <Link
                    to="/blog"
                    className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-cta px-6 py-3 font-semibold text-cta-foreground transition hover:brightness-95"
                  >
                    Visitar el blog <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </article>
            )}
          </div>
        </section>

        {/* TESTIMONIOS */}
        <section className="bg-card/50">
          <div className="mx-auto max-w-7xl px-6 py-16 md:px-8 md:py-24">
            <div className="mb-10 max-w-2xl md:mb-14">
              <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                Confianza
              </span>
              <h2 className="mt-2 text-balance font-display text-4xl font-bold md:text-5xl">
                Opiniones de alumnos
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Reseñas compartidas por estudiantes en Udemy.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="flex h-full flex-col rounded-3xl border border-border border-t-4 border-t-coral bg-card p-7 transition hover:shadow-[var(--shadow-soft)]"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Star className="h-5 w-5 fill-current" />
                      <span className="text-sm font-semibold text-foreground">
                        {t.rating.toLocaleString("es-ES")}/5
                      </span>
                    </div>
                    <span className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-semibold text-secondary">
                      Udemy
                    </span>
                  </div>
                  <p className="mt-5 font-display text-lg leading-relaxed">“{t.quote}”</p>
                  <div className="mt-auto flex items-center gap-3 pt-7">
                    <span className="grid h-11 w-11 place-items-center rounded-full bg-secondary font-sans text-sm font-bold text-cta">
                      {t.initials}
                    </span>
                    <div>
                      <p className="font-sans font-semibold tracking-tight">{t.name}</p>
                      <p className="text-xs text-muted-foreground">Estudiante en Udemy</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GUÍA */}
        <section id="guia" className="bg-secondary text-secondary-foreground">
          <div className="mx-auto grid max-w-7xl gap-8 px-6 py-16 md:grid-cols-[1fr_auto] md:items-center md:px-8 md:py-20">
            <div className="max-w-3xl">
              <span className="text-sm font-semibold uppercase tracking-wider text-cta">
                Guía gratuita
              </span>
              <h2 className="mt-2 text-balance font-display text-4xl font-bold leading-tight md:text-5xl">
                Aprende fotografía desde cero con una ruta clara
              </h2>
              <p className="mt-4 text-lg text-secondary-foreground/75">
                Descubre qué cámara elegir, cómo controlar la exposición, cómo componer y cómo
                empezar a editar tus fotografías.
              </p>
            </div>
            <Link
              to="/guia-principiantes"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cta px-7 py-3.5 font-semibold text-cta-foreground shadow-[var(--shadow-soft)] transition hover:brightness-95"
            >
              Leer la guía <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-secondary/10">
          <div className="mx-auto max-w-4xl px-6 py-16 md:px-8 md:py-24">
            <div className="mb-10 text-center md:mb-14">
              <span className="text-sm font-semibold uppercase tracking-wider text-foreground">
                Preguntas frecuentes
              </span>
              <h2 className="mt-2 text-balance font-display text-4xl font-bold md:text-5xl">
                ¿Tienes alguna duda?
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
                >
                  <summary className="flex cursor-pointer items-center justify-between font-sans text-lg font-semibold tracking-tight">
                    {f.q}
                    <span className="ml-4 grid h-8 w-8 place-items-center rounded-full bg-secondary text-cta transition group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-4 text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CONTACTO */}
        <section id="contacto" className="mx-auto max-w-4xl px-6 py-16 md:px-8 md:py-24">
          <div className="rounded-3xl border border-border bg-card p-8 shadow-[var(--shadow-soft)] md:p-10">
            <div className="mb-8 text-center hidden">
              <h3 className="font-sans text-2xl font-semibold tracking-tight md:text-3xl"></h3>
              <p className="mt-2 text-sm text-muted-foreground"></p>
            </div>
            <HubspotForm
              formId="6ecb5e92-bd52-4dfd-a34c-c07c73267996"
              containerId="hubspot-form-contacto"
              buttonLabel="Mostrar formulario de contacto"
            />
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-foreground text-background">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-12 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <img
                src="/logo.png"
                alt="FotoPasión"
                width={96}
                height={96}
                className="h-10 w-auto"
              />
              <span className="text-lg font-bold text-background">FotoPasion</span>
            </div>
            <p className="mt-3 max-w-md text-sm text-background/70">
              Formación online para aprender fotografía con criterio y práctica.
            </p>
          </div>
          <div className="text-sm text-background/70">© {new Date().getFullYear()} FotoPasión</div>
        </div>
      </footer>
    </div>
  );
}
